// ────────────────────────────────────────────────────────────────────────
// SelectionToolbar — 多选时显示的浮窗工具栏
// ────────────────────────────────────────────────────────────────────────
// 触发条件：选中 ≥2 个普通节点（不含群组节点、不含已在群组中的节点）。
// 浮窗位置：选中节点包围盒上方居中，flow 坐标 → 屏幕坐标手动换算。
// 提供两个动作：
//   - 群组       → onGroupNodes  (装入装饰群组)
//   - 创建工作流 → onCreateGroup (保存为可复用工作流资产)

// ────────────────────────────────────────────────────────────────────────
// SelectionToolbar — 多选时显示的浮窗工具栏
// ────────────────────────────────────────────────────────────────────────
// 触发条件：选中 ≥2 个普通节点（不含群组节点、不含已在群组中的节点）。
// 浮窗位置：选中节点包围盒上方居中，flow 坐标 → 屏幕坐标手动换算。
// 提供两个动作：
//   - 群组       → onGroupNodes  (装入装饰群组)
//   - 创建工作流 → onCreateGroup (保存为可复用工作流资产)

import React from 'react';
import { useViewport } from 'reactflow';
import { Layers, Group, Box } from 'lucide-react';

const SelectionToolbar = ({ selectedNodes, onCreateGroup, onGroupNodes }) => {
  const viewport = useViewport();

  // 群组节点自身和已在群组中的节点都不参与"再次群组"
  const validNodes = selectedNodes?.filter(n => n.type !== 'group' && !n.data?.groupId) || [];
  if (validNodes.length < 2) return null;

  // ── 选中节点包围盒（flow 坐标） ──
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  validNodes.forEach(node => {
    const x = node.position.x;
    const y = node.position.y;
    const w = node.width || node.measured?.width || node.data?.customSize?.width || 200;
    const h = node.height || node.measured?.height || node.data?.customSize?.height || 150;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x + w > maxX) maxX = x + w;
    if (y + h > maxY) maxY = y + h;
  });
  if (minX === Infinity) return null;

  // flow → 屏幕坐标
  const { x: vpX, y: vpY, zoom } = viewport;
  const screenMinX = minX * zoom + vpX;
  const screenMinY = minY * zoom + vpY;
  const screenWidth = (maxX - minX) * zoom;

  const toolbarStyle = {
    position: 'absolute',
    left: screenMinX + screenWidth / 2,
    top: screenMinY - 50,
    transform: 'translateX(-50%)',
    zIndex: 1000,
    background: 'var(--bg-panel)',
    border: '1px solid var(--border-strong)',
    borderRadius: '8px',
    padding: '4px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
    pointerEvents: 'all',
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
  };

  const infoStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  };

  const buttonStyle = {
    ...infoStyle,
    color: 'var(--text-body)',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'all 0.1s',
    background: 'transparent',
    border: 'none',
  };

  const hoverIn = (e) => {
    e.currentTarget.style.color = 'var(--text-hover)';
    e.currentTarget.style.background = 'var(--bg-hover)';
  };
  const hoverOut = (e) => {
    e.currentTarget.style.color = 'var(--text-body)';
    e.currentTarget.style.background = 'transparent';
  };

  return (
    // nodrag/nopan：阻止 ReactFlow 把工具栏内的鼠标事件当作画布拖动/平移
    <div className="nodrag nopan" style={toolbarStyle}>
      {/* 信息：选中数量 */}
      <div style={infoStyle}>
        <Box size={14} />
        <span>{validNodes.length} 个节点</span>
      </div>

      {/* 动作：群组（可选注入；不传则隐藏） */}
      {onGroupNodes && (
        <div style={buttonStyle} onClick={onGroupNodes} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
          <Group size={14} />
          <span>群组</span>
        </div>
      )}

      {/* 动作：保存为工作流 */}
      <div style={buttonStyle} onClick={onCreateGroup} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
        <Layers size={14} />
        <span>创建工作流</span>
      </div>
    </div>
  );
};

export default SelectionToolbar;
